import { getApiAuthorizationHeader } from 'utils/api-auth.utils';

import { RESTFUL_API_BASE_URL } from 'constants/api.constants';

import { RestApiError } from './errors';

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return undefined;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

const getBodyMessage = (body: unknown): string | undefined => {
  if (body == null || typeof body !== 'object') return undefined;

  const message = (body as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() !== ''
    ? message.trim()
    : undefined;
};

const formatRestApiErrorMessage = (
  method: string,
  path: string,
  status: number,
  statusText: string,
  body: unknown,
): string => {
  const trimmedStatusText = statusText.trim();
  const statusLabel = trimmedStatusText
    ? `${status} ${trimmedStatusText}`
    : String(status);
  const bodyMessage = getBodyMessage(body);

  if (
    bodyMessage &&
    bodyMessage.toLowerCase() !== trimmedStatusText.toLowerCase()
  ) {
    return `REST API ${method} ${path} failed: ${statusLabel} — ${bodyMessage}`;
  }

  return `REST API ${method} ${path} failed: ${statusLabel}`;
};

const buildRequestInit = async (
  options: RequestInit,
  requestOptions?: RequestInit,
): Promise<RequestInit> => {
  const merged = { ...options, ...requestOptions };
  const headers = new Headers(options.headers);

  if (requestOptions?.headers) {
    new Headers(requestOptions.headers).forEach((value, key) => {
      headers.set(key, value);
    });
  }

  if (
    merged.body != null &&
    merged.body !== '' &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  headers.set('Authorization', await getApiAuthorizationHeader());

  return {
    ...merged,
    headers,
  };
};

/** Strips error members from Orval's response union — only 2xx survive. */
type SuccessResponse<T> = T extends { status: infer S }
  ? S extends 200 | 201 | 204
    ? T
    : never
  : T;

/**
 * Re-applies success-only narrowing lost by Orval's explicit return types.
 * Use instead of calling generated clients directly in hooks.
 */
export async function restCall<T>(
  request: () => Promise<T>,
): Promise<SuccessResponse<T>> {
  return request() as Promise<SuccessResponse<T>>;
}

/**
 * Orval fetch mutator. Success responses use orval's envelope:
 * `{ data, status, headers }` — not the raw API JSON body.
 */
// ts-unused-exports:disable-next-line
export const restfulRequester = async <T>(
  path: string,
  options: RequestInit,
  requestOptions?: RequestInit,
): Promise<SuccessResponse<T>> => {
  if (!RESTFUL_API_BASE_URL) {
    throw new Error('NEXT_PUBLIC_RESTFUL_API_URL is not configured');
  }

  const requestInit = await buildRequestInit(options, requestOptions);
  const method = (requestInit.method ?? 'GET').toUpperCase();

  const response = await fetch(`${RESTFUL_API_BASE_URL}${path}`, requestInit);

  const parsedBody = await parseResponseBody(response);

  if (!response.ok) {
    throw new RestApiError(
      formatRestApiErrorMessage(
        method,
        path,
        response.status,
        response.statusText,
        parsedBody,
      ),
      response.status,
      parsedBody,
      method,
      path,
    );
  }

  return {
    data: parsedBody,
    status: response.status,
    headers: response.headers,
  } as SuccessResponse<T>;
};
